import OpenSeadragon from "openseadragon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { ViewerMode } from "custom-models/ViewerTypes";

import {
  DEFAULT_EDGE_PAN_CONFIG,
  EDGE_PAN_OVERLAY_TOGGLE_KEY,
  EDGE_PAN_TOGGLE_KEY,
} from "./edgepan.constants";
import {
  CalculatedDeadZones,
  DeadZoneSize,
  EdgePanConfig,
  EdgePanZone,
} from "./edgepan.types";
import OSDClient from "../OSDClient";
import OSDClientEffectiveMagnification from "../OSDClientEffectiveMagnification";

type ViewerClient = OSDClient | OSDClientEffectiveMagnification;

interface UseEdgePanOptions {
  viewerRef: React.MutableRefObject<ViewerClient | null>;
  viewerMode: ViewerMode;
  featureEnabled?: boolean;
  config?: Partial<EdgePanConfig>;
}

interface EdgePanSyntheticMouseEvent {
  clientX: number;
  clientY: number;
}

const calculateDeadZonePixels = (
  size: DeadZoneSize,
  dimensionPx: number
): number => {
  if (size.unit === "px") {
    return size.value;
  }
  return dimensionPx * size.value;
};

export const useEdgePan = ({
  viewerRef,
  viewerMode,
  featureEnabled = true,
  config: configOverrides,
}: UseEdgePanOptions) => {
  const config = useMemo(() => {
    const merged = { ...DEFAULT_EDGE_PAN_CONFIG, ...configOverrides };
    if (configOverrides?.deadZones) {
      merged.deadZones = {
        ...DEFAULT_EDGE_PAN_CONFIG.deadZones,
        ...configOverrides.deadZones,
      };
    }
    return merged;
  }, [configOverrides]);

  const [firstActivate, setFirstActivate] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [active, setActive] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const enabledRef = useRef(enabled);
  const activeRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const mousePositionRef = useRef<{ x: number; y: number } | null>(null);
  const activationTimerRef = useRef<number | null>(null);
  const currentZoneRef = useRef<EdgePanZone | null>(null);
  const viewerElementRef = useRef<HTMLElement | null>(null);
  const viewerRectRef = useRef<DOMRect | null>(null);

  enabledRef.current =
    enabled && featureEnabled && viewerMode === ViewerMode.PAN_ZOOM;

  const toggle = useCallback(() => {
    setEnabled((prev) => !prev);
  }, []);

  const toggleOverlay = useCallback(() => {
    setShowOverlay((prev) => !prev);
  }, []);

  const calculateDeadZones = useCallback(
    (width: number, height: number): CalculatedDeadZones => {
      return {
        top: calculateDeadZonePixels(config.deadZones.top, height),
        bottom: calculateDeadZonePixels(config.deadZones.bottom, height),
        left: calculateDeadZonePixels(config.deadZones.left, width),
        right: calculateDeadZonePixels(config.deadZones.right, width),
      };
    },
    [config.deadZones]
  );

  const calculateZone = useCallback(
    (
      mouseX: number,
      mouseY: number,
      viewerElement: HTMLElement
    ): EdgePanZone | null => {
      const rect =
        viewerRectRef.current || viewerElement.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      const relX = mouseX - rect.left;
      const relY = mouseY - rect.top;

      if (relX < 0 || relX > width || relY < 0 || relY > height) {
        return null;
      }

      // Check if cursor is over the minimap; if so, disable edge panning
      const elementAtPoint = document.elementFromPoint(mouseX, mouseY);
      if (elementAtPoint) {
        let currentElement: Element | null = elementAtPoint;
        while (currentElement && viewerElement.contains(currentElement)) {
          if (currentElement.id?.startsWith("navigator-")) {
            return { type: "dead", direction: { x: 0, y: 0 }, intensity: 0 };
          }
          currentElement = currentElement.parentElement;
        }
      }

      const deadZones = calculateDeadZones(width, height);
      const effectiveWidth = width - deadZones.left - deadZones.right;
      const effectiveHeight = height - deadZones.top - deadZones.bottom;
      const ringWidth = effectiveWidth * config.ringWidthPercent;
      const ringHeight = effectiveHeight * config.ringWidthPercent;

      const distFromLeft = relX;
      const distFromRight = width - relX;
      const distFromTop = relY;
      const distFromBottom = height - relY;

      const inLeftDeadZone = distFromLeft < deadZones.left;
      const inRightDeadZone = distFromRight < deadZones.right;
      const inTopDeadZone = distFromTop < deadZones.top;
      const inBottomDeadZone = distFromBottom < deadZones.bottom;

      if (
        inLeftDeadZone ||
        inRightDeadZone ||
        inTopDeadZone ||
        inBottomDeadZone
      ) {
        return { type: "dead", direction: { x: 0, y: 0 }, intensity: 0 };
      }

      const effectiveLeft = distFromLeft - deadZones.left;
      const effectiveRight = distFromRight - deadZones.right;
      const effectiveTop = distFromTop - deadZones.top;
      const effectiveBottom = distFromBottom - deadZones.bottom;

      const inLeftRing = effectiveLeft < ringWidth;
      const inRightRing = effectiveRight < ringWidth;
      const inTopRing = effectiveTop < ringHeight;
      const inBottomRing = effectiveBottom < ringHeight;

      if (!inLeftRing && !inRightRing && !inTopRing && !inBottomRing) {
        return { type: "dead", direction: { x: 0, y: 0 }, intensity: 0 };
      }

      // Calculate intensity based on distance from inner edge of ring
      let intensity = 0;
      if (inLeftRing) {
        intensity = Math.max(intensity, 1 - distFromLeft / ringWidth);
      }
      if (inRightRing) {
        intensity = Math.max(intensity, 1 - distFromRight / ringWidth);
      }
      if (inTopRing) {
        intensity = Math.max(intensity, 1 - distFromTop / ringHeight);
      }
      if (inBottomRing) {
        intensity = Math.max(intensity, 1 - distFromBottom / ringHeight);
      }

      // Calculate direction based on angle from center to cursor
      const centerX = width / 2;
      const centerY = height / 2;
      const dx = relX - centerX;
      const dy = relY - centerY;
      const magnitude = Math.sqrt(dx * dx + dy * dy);

      let dirX = 0;
      let dirY = 0;
      if (magnitude > 0) {
        dirX = dx / magnitude;
        dirY = dy / magnitude;
      }

      const isCorner =
        (inLeftRing || inRightRing) && (inTopRing || inBottomRing);

      return {
        type: isCorner ? "corner" : "edge",
        direction: { x: dirX, y: dirY },
        intensity: Math.min(1, intensity),
      };
    },
    [config.ringWidthPercent, calculateDeadZones]
  );

  const calculateSpeed = useCallback(
    (intensity: number): number => {
      const easedIntensity = Math.pow(intensity, config.easeInExponent);
      return (
        config.minSpeed + easedIntensity * (config.maxSpeed - config.minSpeed)
      );
    },
    [config.minSpeed, config.maxSpeed, config.easeInExponent]
  );

  const panLoop = useCallback(() => {
    if (!enabledRef.current || !activeRef.current) {
      return;
    }

    const viewer = viewerRef.current?.viewer;
    const viewerElement = viewerElementRef.current;

    if (!viewer?.viewport || !viewerElement || !mousePositionRef.current) {
      animationFrameRef.current = requestAnimationFrame(panLoop);
      return;
    }

    const zone = calculateZone(
      mousePositionRef.current.x,
      mousePositionRef.current.y,
      viewerElement
    );

    currentZoneRef.current = zone;

    if (zone && zone.type !== "dead") {
      const speed = calculateSpeed(zone.intensity);
      const pixelDelta = new OpenSeadragon.Point(
        zone.direction.x * speed,
        zone.direction.y * speed
      );
      const viewportDelta = viewer.viewport.deltaPointsFromPixels(pixelDelta);

      // Check bounds to prevent panning beyond image edges
      const currentBounds = viewer.viewport.getBounds();
      const imageBounds = viewer.world.getHomeBounds();

      let allowedDelta = { x: viewportDelta.x, y: viewportDelta.y };

      // Check horizontal movement
      if (viewportDelta.x < 0) {
        // Panning left - check if right edge of viewport would go past left edge of image
        const rightEdgeAfterPan =
          currentBounds.x + currentBounds.width + viewportDelta.x;

        if (rightEdgeAfterPan < imageBounds.x) {
          allowedDelta.x = 0;
        }
      } else if (viewportDelta.x > 0) {
        // Panning right - check if left edge of viewport would go past right edge of image
        const leftEdgeAfterPan = currentBounds.x + viewportDelta.x;

        if (leftEdgeAfterPan > imageBounds.x + imageBounds.width) {
          allowedDelta.x = 0;
        }
      }

      // Check vertical movement
      if (viewportDelta.y < 0) {
        // Panning up - check if bottom edge of viewport would go past top edge of image
        const bottomEdgeAfterPan =
          currentBounds.y + currentBounds.height + viewportDelta.y;

        if (bottomEdgeAfterPan < imageBounds.y) {
          allowedDelta.y = 0;
        }
      } else if (viewportDelta.y > 0) {
        // Panning down - check if top edge of viewport would go past bottom edge of image
        const topEdgeAfterPan = currentBounds.y + viewportDelta.y;

        if (topEdgeAfterPan > imageBounds.y + imageBounds.height) {
          allowedDelta.y = 0;
        }
      }

      if (allowedDelta.x !== 0 || allowedDelta.y !== 0) {
        const finalDelta = new OpenSeadragon.Point(
          allowedDelta.x,
          allowedDelta.y
        );

        viewer.viewport.panBy(finalDelta, false);
      }
    }

    animationFrameRef.current = requestAnimationFrame(panLoop);
  }, [calculateZone, calculateSpeed]);

  const startPanning = useCallback(() => {
    if (activeRef.current) return;
    activeRef.current = true;
    setActive(true);
    animationFrameRef.current = requestAnimationFrame(panLoop);
  }, [panLoop]);

  const stopPanning = useCallback(() => {
    activeRef.current = false;
    setActive(false);
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (activationTimerRef.current !== null) {
      clearTimeout(activationTimerRef.current);
      activationTimerRef.current = null;
    }
    currentZoneRef.current = null;
  }, []);

  const handleMouseMove = useCallback(
    (event: MouseEvent | EdgePanSyntheticMouseEvent) => {
      mousePositionRef.current = { x: event.clientX, y: event.clientY };

      if (!enabledRef.current) {
        stopPanning();
        return;
      }

      const viewerElement = viewerElementRef.current;
      if (!viewerElement) return;

      const zone = calculateZone(event.clientX, event.clientY, viewerElement);

      if (zone && zone.type !== "dead") {
        if (!activeRef.current && activationTimerRef.current === null) {
          activationTimerRef.current = window.setTimeout(() => {
            activationTimerRef.current = null;
            if (enabledRef.current) {
              startPanning();
            }
          }, config.activationDelayMs);
        }
      } else {
        stopPanning();
      }
    },
    [calculateZone, startPanning, stopPanning, config.activationDelayMs]
  );

  const handleMouseLeave = useCallback(() => {
    mousePositionRef.current = null;
    stopPanning();
  }, [stopPanning]);

  useHotkeys(
    EDGE_PAN_TOGGLE_KEY,
    () => {
      setEnabled((prev) => !prev);
      firstActivate &&
        mousePositionRef.current &&
        handleMouseMove({
          clientX: mousePositionRef.current.x,
          clientY: mousePositionRef.current.y,
        });
      setFirstActivate(true);
    },
    {
      enabled: featureEnabled,
      scopes: ["viewerHotkeys"],
      enableOnFormTags: false,
    },
    [firstActivate, handleMouseMove]
  );

  useHotkeys(
    EDGE_PAN_OVERLAY_TOGGLE_KEY,
    () => {
      setShowOverlay((prev) => !prev);
    },
    {
      enabled: featureEnabled,
      scopes: ["viewerHotkeys"],
      enableOnFormTags: false,
    },
    []
  );

  useHotkeys(
    "Escape",
    () => {
      stopPanning();
      setEnabled(false);
      setShowOverlay(false);
    },
    { enabled: featureEnabled, scopes: ["viewerHotkeys"] },
    [stopPanning]
  );

  useEffect(() => {
    if (!featureEnabled) return;

    const viewerElement = document.getElementById("viewer");
    if (!viewerElement) return;

    viewerElementRef.current = viewerElement;
    viewerRectRef.current = viewerElement.getBoundingClientRect();

    // Update rect on window resize
    const updateRect = () => {
      if (viewerElementRef.current) {
        viewerRectRef.current =
          viewerElementRef.current.getBoundingClientRect();
      }
    };

    window.addEventListener("resize", updateRect);
    viewerElement.addEventListener("mousemove", handleMouseMove);
    viewerElement.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("resize", updateRect);
      viewerElement.removeEventListener("mousemove", handleMouseMove);
      viewerElement.removeEventListener("mouseleave", handleMouseLeave);
      viewerElementRef.current = null;
      viewerRectRef.current = null;
      stopPanning();
    };
  }, [featureEnabled, handleMouseMove, handleMouseLeave, stopPanning]);

  // Stop panning if viewer mode changes or pan is toggled off
  useEffect(() => {
    if (!enabled || viewerMode !== ViewerMode.PAN_ZOOM) {
      stopPanning();
    }
  }, [enabled, viewerMode, stopPanning]);

  // Stop panning on unmount
  useEffect(() => {
    return () => {
      stopPanning();
    };
  }, [stopPanning]);

  return {
    enabled,
    active,
    showOverlay:
      showOverlay && featureEnabled && viewerMode === ViewerMode.PAN_ZOOM,
    toggle,
    toggleOverlay,
  };
};
